import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import { OutlinedTextFieldProps } from '@mui/material';
import toast from 'react-hot-toast';
import {
  API_E2E_POST_SEND_VERIFICATION,
  API_E2E_POST_VALIDATE_APPLICANT,
  API_E2E_POST_VERIFY_CHECK_CREATE_ACCOUNT,
  API_E2E_POST_CREATE_ACCOUNT_WITHOUT_OTP,
} from 'src/api/e2e-request-objects';
import {
  E2eBaseCallDetailsI,
  e2eGetBaseAuthenticatedRequests,
  e2eGetBasePublicRequests,
} from 'src/api/query-logic/e2e-query-base-calls';
import { apiRequest, otpRequest } from 'src/api/request-handler';
import PhoneMask from 'src/components/masks/phone-mask';
import { AuthContextType } from 'src/contexts/auth/jwt-context';
import { E2eLogicObjectsWithParams } from 'src/e2e-redesign/business-logic/e2e-logic-utils';
import { E2eRoutingArgsI, handleE2eRoutingLogic } from 'src/e2e-redesign/business-logic/e2e-routing-logic';
import { ComponentLogicI } from 'src/e2e-redesign/interfaces/e2e-base-interfaces';
import { ApplicationStage } from 'src/enums/aaso.enums';
import { E2ERoutes } from 'src/enums/routes.enums';
import { fsTrackEvent } from 'src/utils/fullstory';

export const createAccountLogic_init = (routingPackage: E2eLogicObjectsWithParams) => {
  const params = routingPackage.params;
  const { store_uuid, e2e_form_id, store_name } = params;
  const { e2eStore, auth } = routingPackage;
  const { isAuthenticated } = auth;
  const baseCallDetails: E2eBaseCallDetailsI = {
    store_uuid: params.store_uuid,
    e2e_form_id: params.e2e_form_id,
  };
  // this runs if user is not authenticated
  const publicRequests = e2eGetBasePublicRequests(e2eStore, baseCallDetails, isAuthenticated);

  // this runs if they are
  const authenticatedRequests = e2eGetBaseAuthenticatedRequests(baseCallDetails, e2eStore, isAuthenticated);

  useEffect(() => {
    if (store_uuid && store_name && e2e_form_id) {
      e2eStore.initApplicationStore(store_uuid, store_name, e2e_form_id);
    }
  }, []);

  return {
    publicRequests,
    authenticatedRequests,
    isDataLoading: authenticatedRequests.appSettingsIsLoading || publicRequests.appConfigIsLoading
  };
};

export enum CreateAccountSectionsE {
  'form' = 'form',
  'comms' = 'comms',
  'otp' = 'otp',
}

export interface CreateAccountFormValuesI {
  first_name: string;
  last_name: string;
  mobile_phone: string;
  email: string;
  password: string;
  confirm_password: string;
  fp_myfp_tc: {
    diclosure_id: string;
    accepted: boolean;
  };
  fp_customer_notice: {
    diclosure_id: string;
    accepted: boolean;
  };
  fp_privacy: {
    diclosure_id: string;
    accepted: boolean;
  };
  fp_bullets: {
    diclosure_id: string;
    accepted: boolean;
  };
  multi_check: boolean;
}

export interface CreateAccountCommsValuesI {
  fp_sms_consent: {
    diclosure_id: string;
    accepted: boolean;
  };
  fp_additional_communications: {
    diclosure_id: string;
    accepted: boolean;
  };
}
export interface CreateAccountSectionValuesI {
  form: CreateAccountFormValuesI;
  comms: CreateAccountCommsValuesI;
}
export interface CreateAccountLogicI extends ComponentLogicI {
  fn: {
    validateUserForm: (values: CreateAccountFormValuesI) => Promise<boolean>;
    handleCommunicationNotice: (values: CreateAccountCommsValuesI) => void;
    submitForm: (code: string) => Promise<any>;
    getInputProps: (name: string, endAdornment: any) => Partial<OutlinedTextFieldProps['InputProps']>;
    handleIsDisabled: (values: CreateAccountFormValuesI) => boolean;
    sendOtp: () => void;
    shouldRequireOtp: () => boolean;
  };
  v: {
    section?: CreateAccountSectionsE;
    sectionValues: CreateAccountSectionValuesI;
    error?: string;
  };
}
const sv = {
  //always true for the form now
  form: {
    first_name: '',
    last_name: '',
    mobile_phone: '',
    email: '',
    password: '',
    confirm_password: '',
    fp_myfp_tc: {
      diclosure_id: '',
      accepted: true,
    },
    fp_customer_notice: {
      diclosure_id: '',
      accepted: true,
    },
    fp_privacy: {
      diclosure_id: '',
      accepted: true,
    },
    fp_bullets: {
      diclosure_id: '',
      accepted: true,
    }, // this is auto true since there is no check
    multi_check: false,
  },
  comms: {
    fp_sms_consent: {
      diclosure_id: '',
      accepted: false,
    },
    fp_additional_communications: {
      diclosure_id: '',
      accepted: false,
    },
  },
};
export const createAccountLogic_funcAndVals = (routingPackage: E2eLogicObjectsWithParams): CreateAccountLogicI => {
  const e2eStore = routingPackage.e2eStore;
  const auth = routingPackage.auth;
  const app_configurations = e2eStore.app_configurations;
  const { store_uuid, e2e_form_id } = routingPackage.params;
  const createAaso = e2eStore.createAaso;
  const [section, setSection] = useState<CreateAccountSectionsE>(CreateAccountSectionsE.form);
  const [sectionValues, setSectionValues] = useState<CreateAccountSectionValuesI>(sv);
  const [sentOtp, setSentOtp] = useState<boolean>(false);
  const [submittingAaso, setSubmittingAaso] = useState(false);
  const [error, setError] = useState<string>();

  const createAasoAndRoute = useCallback(
    async (a: AuthContextType) => {
      if (a && a.isAuthenticated && a.user && !submittingAaso) {
        const user = a.user;
        if (store_uuid && e2e_form_id && user && app_configurations) {
          try {
            setSubmittingAaso(true);
            const generatedAASO = await createAaso(user, store_uuid, e2e_form_id);
            routingPackage.aasoContext = { aaso: generatedAASO };
            const routingArgs: E2eRoutingArgsI = {
              currentStage: ApplicationStage.STARTED,
              currentE2eRoute: E2ERoutes.Welcome,
            };
            handleE2eRoutingLogic(routingPackage, routingArgs, generatedAASO);
          } catch (error) {
            console.error('Error creating AASO:', error);
          } finally {
            setSubmittingAaso(false);
          }
        }
      }
    },
    [store_uuid, e2e_form_id, app_configurations, routingPackage, handleE2eRoutingLogic, submittingAaso],
  );

  useEffect(() => {
    createAasoAndRoute(auth);
  }, [auth, app_configurations, createAasoAndRoute]);

  useEffect(() => {
    if (section === CreateAccountSectionsE.otp) {
      sendOtp();
    }
  }, [section]);

  const isPassword = (name: string) => name === 'password' || name === 'confirm_password';

  function getInputProps(name: string, endAdornment: any): Partial<OutlinedTextFieldProps['InputProps']> {
    const inputProps: any = {};

    if (isPassword(name)) {
      inputProps.endAdornment = endAdornment();
    }

    if (name === 'mobile_phone') {
      inputProps.inputComponent = PhoneMask;
    }
    return inputProps;
  }

  const handleIsDisabled = (values: CreateAccountFormValuesI) => {
    return !values.multi_check || values.password !== values.confirm_password;
  };

  const validateUserForm = async (values: CreateAccountFormValuesI): Promise<boolean> => {
    // we are going to check if user already exists
    // R:TODO E2E P0 - just verify the below items have been done
    /**
     * - check that email doesn't already exist
     * - remember to lowercase all data (other than password)
     * - we need to check that their password meets standards and matches
     * - verify all data points (we can't come back so we need to check it now)
     * - we should also save the users form data via redis_cache ttl 24hrs
     *    - check against that form... make sure the users information is the same
     *    - save all data in lowercase and strip whitespace.. mobile number should be stripped of all non-numeric characters
     *    - no national code on mobile number
     */
    values.first_name = values.first_name.trim().toLowerCase();
    values.last_name = values.last_name.trim().toLowerCase();
    const res = await apiRequest(API_E2E_POST_VALIDATE_APPLICANT, { body: { applicant: values } });
    if (res) {
      if (res.emailExists) {
        toast.error('Email already exists');
        return false;
      }
      if (res.isValidForm) {
        setSectionValues({ ...sectionValues, form: values });
        setSection(CreateAccountSectionsE.comms);
        return true;
      } else {
        toast.error('Invalid form');
        return false;
      }
    }
    return false;
  };

  const getExternalIdIfExists = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const e_id = urlParams.get('e_id');
    const p_id = urlParams.get('p_id');

    if (e_id || p_id) {
      return { e_id, p_id };
    }
    return null;
  };

  const handleCommunicationNotice = async (values: any) => {
    const updatedSectionValues = { ...sectionValues, comms: values };
    setSectionValues(updatedSectionValues);

    if (shouldRequireOtp()) {
      setSection(CreateAccountSectionsE.otp);
    } else {
      // Skip OTP section and create account directly
      const email = updatedSectionValues.form.email;
      const password = updatedSectionValues.form.password;
      const externalId = getExternalIdIfExists();

      try {
        const res = await apiRequest(API_E2E_POST_CREATE_ACCOUNT_WITHOUT_OTP, {
          body: {
            data: externalId ? { ...updatedSectionValues, ...externalId } : updatedSectionValues,
          },
        });

        if (res && res.status === 'approved') {
          auth.signIn(email, password);
          fsTrackEvent('Applicant Account Created', {
            email,
          });
        }
      } catch (e) {
        setError(e.message);
      }
    }
  };

  const sendOtp = async (bypass?: boolean) => {
    if (sentOtp && !bypass) return;
    try {
      fsTrackEvent('Send OTP', {
        store_name: app_configurations?.store_details?.store_name,
        store_uuid,
        store_id: app_configurations?.store_details?.id,
      });
    } catch (e) {}
    try {
      const email = sectionValues.form.email;
      const mobile = sectionValues.form.mobile_phone;

      const cleansedMobile = mobile.replace(/\D/g, '');
      await otpRequest(API_E2E_POST_SEND_VERIFICATION, { body: { toEmail: email, toSms: cleansedMobile } });
      setSentOtp(true);
      setError(undefined);
    } catch (e) {
      toast.error(e.message);
      setError(e.message);
    }
  };

  //should probably move this to a utils file
  const formatPhone = (phone: string) => (phone.startsWith('+1') ? phone : `+1${phone}`);

  // Check if any lender requires OTP
  const shouldRequireOtp = useCallback(() => {
    const lenders = app_configurations?.lenders || [];
    return lenders.some(lender => lender.otp_required === true);
  }, [app_configurations?.lenders]);

  const verifyAndCreateAccount = async (code: string): Promise<any> => {
    const mobile = sectionValues.form.mobile_phone;
    // R:TODO E2E P0 - verify that disclosures are being captured correctly
    const cleansedMobile = mobile.replace(/\D/g, '');
    const externalId = getExternalIdIfExists();
    return await otpRequest(API_E2E_POST_VERIFY_CHECK_CREATE_ACCOUNT, {
      body: {
        otp_code: { to: formatPhone(cleansedMobile), code },
        data: externalId ? { ...sectionValues, ...externalId } : sectionValues,
      },
    });
  };

  const createAccountWithoutOtp = async (): Promise<any> => {
    const externalId = getExternalIdIfExists();
    return await apiRequest(API_E2E_POST_CREATE_ACCOUNT_WITHOUT_OTP, {
      body: {
        data: externalId ? { ...sectionValues, ...externalId } : sectionValues,
      },
    });
  };

  const submitForm = async (code: string) => {
    try {
      const email = sectionValues.form.email;
      const password = sectionValues.form.password;

      let res;
      if (shouldRequireOtp()) {
        // Use OTP verification endpoint
        res = await verifyAndCreateAccount(code);
      } else {
        // Skip OTP verification
        res = await createAccountWithoutOtp();
      }

      if (res && res.status === 'approved') {
        auth.signIn(email, password);
        fsTrackEvent('Applicant Account Created', {
          email,
        });
      }
      return res;
    } catch (e) {
      toast.error(e.message);
    }
  };

  return {
    fn: {
      validateUserForm,
      handleCommunicationNotice,
      submitForm,
      getInputProps,
      handleIsDisabled,
      sendOtp,
      shouldRequireOtp,
    },
    v: {
      section,
      sectionValues,
      error,
    },
  };
};
